import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "playfieldvr:onsite:disabledGames";

const read = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
};

export const useOnsiteAvailability = () => {
  const [disabled, setDisabled] = useState<string[]>(() => read());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setDisabled(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const persist = useCallback((next: string[]) => {
    setDisabled(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, []);

  const isDisabled = useCallback((slug: string) => disabled.includes(slug), [disabled]);

  const toggleGame = useCallback(
    (slug: string) => {
      const next = disabled.includes(slug)
        ? disabled.filter((s) => s !== slug)
        : [...disabled, slug];
      persist(next);
    },
    [disabled, persist],
  );

  const resetAll = useCallback(() => persist([]), [persist]);

  return { disabled, isDisabled, toggleGame, resetAll };
};
